import React, {useCallback, useContext, useEffect, useState} from 'react';

import {FormatSettingsSection} from 'src/components/format-settings';
import {DISCARD_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';

import {
    AccessControlsSection,
    useDomains,
    useEzProxy,
    useIpRanges,
} from './sections/access-controls';
import {AccountSettingsSection, useAccountSettings} from './sections/account-settings';
import {useAccountPreset} from './sections/entitlement-settings/useAccountPreset';
import {useDefaultFormatSettings, useFormatSettings} from './sections/format-settings';
import {WelcomeEmailCustomizationsSection, useWelcomeEmailCustomizations} from './sections/welcome-email-customizations';

import type {MessageContextType} from 'src/contexts/message-context';

type AccountSettingsContent = {
    isActionsDisabled: boolean,
    isChunkChanged: boolean,
    content: React.ReactNode,
    saveBusy: boolean,
    onSave: () => void,
    onDiscard: () => void,
};

type Props = {
    accountId: number,
    onProceed: () => void,
    children: (content: AccountSettingsContent) => React.ReactElement,
    disabled?: boolean,
    isAccountEdit?: boolean,
    onFieldChange: (section: string) => (parameterName: string) => void,
};

export const AccountSettingsController = (props: Props) => {
    const {accountId, children, onProceed, disabled, onFieldChange, isAccountEdit} = props;
    const [saveBusy, setSaveBusy] = useState(false);
    const [showPresetValidation, setShowPresetValidation] = useState(false);
    const {setErrorMessage, setSuccessMessage} = useContext<MessageContextType>(MessageContext);


    const {isCurrentChunkChanged, proceed, setIsChunkChanged} = useConfirmationChunk({
        chunkName: 'account-settings',
        onProceed,
    });

    const {
        isLoading: accountSettingsLoading,
        loadingError: accountSettingsLoadingError,
        data: accountSettings,
        setData: setAccountSettings,
        isChanged: isChangedAccountSettings,
        saveChanges: saveAccountSettings,
        discardChanges: discardAccountSettings,
    } = useAccountSettings({accountId});

    const {
        isLoading: welcomeEmailCustomizationsLoading,
        loadingError: welcomeEmailCustomizationsLoadingError,
        data: welcomeEmailCustomizations,
        setData: setWelcomeEmailCustomizations,
        isChanged: isWelcomeEmailCustomizationsChanged,
        saveChanges: saveWelcomeEmailCustomizations,
        discardChanges: discardWelcomeEmailCustomizations,
    } = useWelcomeEmailCustomizations({accountId});

    const {
        isLoading: isDefaultFormatSettingsLoading,
        loadingError: defaultFormatSettingsLoadingError,
        data: defaultLocale,
    } = useDefaultFormatSettings({accountId});

    const {
        isLoading: formatSettingsLoading,
        loadingError: formatSettingsLoadingError,
        data: locale,
        setData: setAccountLocale,
        isChanged: isChangedAccountLocale,
        saveChanges: onSaveAccountLocale,
        discardChanges: onDiscardAccountLocale,
    } = useFormatSettings({accountId});

    const {
        isLoading: ipRangesLoading,
        data: ipRanges,
        loadingError: ipRangesLoadingError,
        setData: setIpRanges,
        isChanged: isIpRangesChanged,
        saveChanges: saveIpRanges,
        discardChanges: discardIpRanges,

        setLastEditedRange,
    } = useIpRanges({accountId});

    const {
        isLoading: domainsLoading,
        loadingError: domainsLoadingError,
        data: domains,
        setData: setDomains,
        isChanged: isDomainsChanged,
        saveChanges: saveDomains,
        discardChanges: discardDomains,

        busyDomains,
        setLastEditedDomain,
    } = useDomains({accountId});

    const {
        isLoading: proxyLoading,
        loadingError: proxyLoadingError,
        data: proxy,
        setData: setProxy,
        isChanged: isEzProxyChanged,
        saveChanges: saveProxy,
        discardChanges: discardProxy,
    } = useEzProxy({accountId});

    const {
        preset,
        loading: presetLoading,
        error: presetError,
        setPreset,
        isChanged: isChangedAccountPreset,
        discardChanges: discardAccountPreset,
        savePreset,
    } = useAccountPreset(accountId);

    useEffect(() => {
        if (preset?.presetId) {
            setShowPresetValidation(false);
        }
    }, [preset]);

    useEffect(() => {
        const changed = [
            isChangedAccountSettings,
            isChangedAccountLocale,
            isIpRangesChanged,
            isDomainsChanged,
            isEzProxyChanged,
            isChangedAccountPreset,
            isWelcomeEmailCustomizationsChanged,
        ].some(Boolean);

        setIsChunkChanged(changed);
    }, [
        setIsChunkChanged,
        isChangedAccountSettings,
        isChangedAccountLocale,
        isIpRangesChanged,
        isDomainsChanged,
        isEzProxyChanged,
        isChangedAccountPreset,
        isWelcomeEmailCustomizationsChanged,
    ]);

    const saveChanges = useCallback(async () => {
        const calls = [];

        if ((!preset || !preset.presetId)) {
            setShowPresetValidation(true);
            setErrorMessage('Please select an Entitlement Settings preset before saving.');
            return;
        }

        setSaveBusy(isChangedAccountPreset);

        if (isChangedAccountPreset) {
            calls.push(await savePreset());
        }

        if (isChangedAccountSettings) {
            calls.push(await saveAccountSettings());
        }

        if (isChangedAccountLocale) {
            calls.push(await onSaveAccountLocale());
        }

        if (isIpRangesChanged) {
            calls.push(await saveIpRanges());
        }

        if (isDomainsChanged) {
            calls.push(await saveDomains());
        }

        if (isEzProxyChanged) {
            calls.push(await saveProxy());
        }

        if (isWelcomeEmailCustomizationsChanged) {
            calls.push(await saveWelcomeEmailCustomizations());
        }
        setSaveBusy(false);

        const error = [...new Set(calls)].filter(Boolean).join(' ');

        if (error) {
            setErrorMessage(error);
        } else {
            proceed();
        }
    }, [
        isChangedAccountSettings,
        isChangedAccountLocale,
        isIpRangesChanged,
        isDomainsChanged,
        isEzProxyChanged,
        saveAccountSettings,
        onSaveAccountLocale,
        isChangedAccountPreset,
        saveIpRanges,
        saveDomains,
        saveProxy,
        proceed,
        setErrorMessage,
        savePreset,
        preset,
        isWelcomeEmailCustomizationsChanged,
        saveWelcomeEmailCustomizations,
    ]);

    const discardChanges = useCallback(() => {
        discardAccountSettings();
        onDiscardAccountLocale();
        discardAccountPreset();
        discardIpRanges();
        discardDomains();
        discardProxy();
        discardWelcomeEmailCustomizations();

        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [
        discardAccountSettings,
        onDiscardAccountLocale,
        discardAccountPreset,
        setSuccessMessage,
        discardIpRanges,
        discardDomains,
        discardProxy,
        discardWelcomeEmailCustomizations,
    ]);

    return children({
        isActionsDisabled: !isCurrentChunkChanged,
        content: [
            <AccountSettingsSection
                key="AccreditationSection"
                {...{
                    isAccountEdit,
                    disabled,
                    accountSettingsLoading,
                    accountSettingsLoadingError,
                    accountSettings,
                    onChange: setAccountSettings,
                    setPreset,
                    preset,
                    onFieldChange: onFieldChange('Account Settings'),
                    showPresetValidation,
                }}
            />,
            <FormatSettingsSection
                key="FormatSettingsSection"
                {...{
                    disabled,
                    isLoading: formatSettingsLoading || isDefaultFormatSettingsLoading,
                    loadingError: formatSettingsLoadingError || defaultFormatSettingsLoadingError,
                    defaultLocale,
                    locale,
                    onChange: setAccountLocale,
                    onFieldChange: onFieldChange('Format Settings'),
                }}
            />,
            <AccessControlsSection
                key="AccessControlsSection"
                {...{
                    disabled,

                    ipRangesLoading,
                    ipRangesLoadingError,
                    ipRanges,
                    setIpRanges,
                    setLastEditedRange,

                    domainsLoading,
                    domainsLoadingError,
                    busyDomains,
                    domains,
                    setDomains,
                    setLastEditedDomain,

                    proxyLoading,
                    proxyLoadingError,
                    presetLoading,
                    presetError,
                    proxy,
                    setProxy,
                    onFieldChange: onFieldChange('Access Controls'),
                }}
            />,
            <WelcomeEmailCustomizationsSection
                key="WelcomeEmailCustomizationsSection"
                disabled={disabled}
                isLoading={welcomeEmailCustomizationsLoading}
                loadingError={welcomeEmailCustomizationsLoadingError}
                customizations={welcomeEmailCustomizations}
                onChange={data => {
                    onFieldChange('Welcome Email Customizations')('LLM Only Welcome Email Resource Link');
                    return setWelcomeEmailCustomizations(data);
                }}
            />,
        ],
        isChunkChanged: isCurrentChunkChanged,
        saveBusy,
        onSave: saveChanges,
        onDiscard: discardChanges,
    });
};
